/**
 * columnRows — small adapter that flattens a `DdlStructure` (table arm) into
 * the row shape consumed by the read-only `ColumnsTab`. For `create-table` it
 * surfaces the `columns[]` array verbatim. For `alter-table` it surfaces the
 * column-flavoured clauses (`add-column`, `drop-column`, `rename-column`) as
 * one row per clause; `opaque` clauses are filtered out (rendered separately
 * in a future Phase A slice).
 *
 * Slice A2: rows carry an optional `identity?: IdentitySpec` so `ColumnsTab`
 * can surface an "IDENTITY" badge when the structural data exposes it
 * (post-edit columns, or DSE2 extractor enhancements). When `identity` is
 * absent the badge is suppressed.
 *
 * Slice 0.4 read-only render scaffolding — pure data adapter, no React.
 */
import type { IdentifierQuoting } from '../../../../sql-grammar/model';
import type { ExistingNameOrigin } from '../../../../sql-resolver/ddl/apply/dialect.quoting';
import type { DdlStructure, IdentitySpec } from '../../../../sql-resolver/types/ddlEdit';

export type ColumnRowAction = 'declared' | 'added' | 'dropped' | 'renamed';

export interface ColumnRow {
  readonly key: string;
  readonly name: string;
  readonly typeRef: string | null;
  readonly nullable: boolean | null;
  readonly defaultText: string | null;
  readonly action: ColumnRowAction;
  readonly previousName: string | null;
  readonly identity: IdentitySpec | null;
  /** WHICH occurrence this row is, in the source's own terms — the answer
   *  `name` on its own cannot give. A column's identity is its bytes PLUS its
   *  delimiters, so `CREATE TABLE t (Id integer, "Id" integer)` declares two
   *  different columns on a folding engine and both rows read `Id`. The row IS
   *  the occurrence the user clicked, so it reports the declaration it came
   *  from and `ColumnsTab` passes that into the `alter-column` payload as
   *  `columnNameOrigin`; without it `applyDdlEdit` refuses such a request
   *  rather than guessing (`columnEdit.alterTable.ts`'s
   *  `sourceSpellingOrigin`).
   *
   *  `null` on a DROP COLUMN row only: the clause records no spelling for a
   *  column it removes, and that row carries no Edit affordance to spend one
   *  on. Everywhere else an unrecorded quoting reads as `'bare-reference'` —
   *  the same posture the evidence layer holds, so a row and the source answer
   *  alike. */
  readonly nameOrigin: ExistingNameOrigin | null;
}

const PLACEHOLDER_TYPE = '—';

/** The origin a DECLARED spelling asserts. Delimiters suppress the engine's
 *  fold, so a name the source wrote delimited is the catalog's own bytes; a
 *  bare one — or one whose quoting the extractor did not record — is a
 *  reference the engine folds for itself. */
function originOfQuoting(quoting: IdentifierQuoting | undefined): ExistingNameOrigin {
  return quoting !== undefined && quoting !== 'unquoted' ? 'delimited-source' : 'bare-reference';
}

export function columnRowsFromStructure(
  structure: DdlStructure | null | undefined,
): readonly ColumnRow[] {
  if (!structure) return [];
  if (structure.kind === 'create-table') {
    return structure.columns.map((col, i) => ({
      key: `declared-${i}-${col.name}`,
      name: col.name,
      typeRef: col.typeRef ?? PLACEHOLDER_TYPE,
      nullable: typeof col.nullable === 'boolean' ? col.nullable : null,
      defaultText: col.defaultText ?? null,
      action: 'declared' as const,
      previousName: null,
      identity: col.identity ?? null,
      nameOrigin: originOfQuoting(col.nameQuoting),
    }));
  }
  if (structure.kind === 'alter-table') {
    const rows: ColumnRow[] = [];
    structure.clauses.forEach((clause, i) => {
      if (clause.kind === 'add-column') {
        rows.push({
          key: `added-${i}-${clause.column.name}`,
          name: clause.column.name,
          typeRef: clause.column.typeRef ?? PLACEHOLDER_TYPE,
          nullable: typeof clause.column.nullable === 'boolean' ? clause.column.nullable : null,
          defaultText: clause.column.defaultText ?? null,
          action: 'added',
          previousName: null,
          identity: clause.column.identity ?? null,
          nameOrigin: originOfQuoting(clause.nameQuoting),
        });
      } else if (clause.kind === 'drop-column') {
        rows.push({
          key: `dropped-${i}-${clause.columnName}`,
          name: clause.columnName,
          typeRef: null,
          nullable: null,
          defaultText: null,
          action: 'dropped',
          previousName: null,
          identity: null,
          nameOrigin: null,
        });
      } else if (clause.kind === 'rename-column') {
        rows.push({
          key: `renamed-${i}-${clause.oldName}-${clause.newName}`,
          name: clause.newName,
          typeRef: null,
          nullable: null,
          defaultText: null,
          action: 'renamed',
          previousName: clause.oldName,
          identity: null,
          // The row shows the name the column answers to AFTER this clause,
          // so the NEW name's delimiters are the ones that identify it.
          nameOrigin: originOfQuoting(clause.newNameQuoting),
        });
      }
      // 'opaque' clauses intentionally skipped — rendered in a Phase A slice.
    });
    return rows;
  }
  return [];
}
